import { Box } from "@chakra-ui/react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Alignment,
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  FontFamily,
  FontSize,
  Heading,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  Strikethrough,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableToolbar,
  Underline,
  Undo,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

const editorConfig = {
  licenseKey: "GPL",
  plugins: [
    Alignment,
    BlockQuote,
    Bold,
    Essentials,
    FontFamily,
    FontSize,
    Heading,
    Italic,
    Link,
    List,
    ListProperties,
    Paragraph,
    Strikethrough,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableToolbar,
    Underline,
    Undo,
  ],
  toolbar: {
    items: [
      "undo",
      "redo",
      "|",
      "heading",
      "|",
      "fontFamily",
      "fontSize",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "link",
      "blockQuote",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "|",
      "insertTable",
    ],
    shouldNotGroupWhenFull: true,
  },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
      { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
      { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
    ],
  },
  fontFamily: {
    options: [
      "default",
      "Arial, Helvetica, sans-serif",
      "Georgia, serif",
      "Tahoma, Geneva, sans-serif",
      "Times New Roman, Times, serif",
      "Trebuchet MS, Helvetica, sans-serif",
      "Verdana, Geneva, sans-serif",
    ],
    supportAllValues: true,
  },
  fontSize: {
    options: [10, 12, 14, "default", 18, 20, 24, 28, 32],
    supportAllValues: true,
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "toggleTableCaption",
      "tableCellProperties",
    ],
  },
  link: {
    addTargetToExternalLinks: true,
  },
  placeholder: "Write the company policy content here. Use Insert Table to add tabular policy details.",
};

const CompanyPolicyEditor = ({ value, onChange, isDisabled = false }) => {
  const handleReady = (editor) => {
    editor.keystrokes.set("Backspace", (keyEvtData, cancel) => {
      const position = editor.model.document.selection.getFirstPosition();

      if (!position || !position.parent || position.offset !== 0) {
        return;
      }

      const currentBlock = position.parent;
      const previousBlock = currentBlock.previousSibling;
      const isParagraph = currentBlock.name === "paragraph";
      const isAfterHeading = ["heading1", "heading2", "heading3"].includes(previousBlock?.name);

      if (isParagraph && isAfterHeading) {
        cancel();
      }
    });
  };

  return (
    <Box
      sx={{
        ".ck.ck-editor": {
          border: "1px solid #CFD6DC",
          borderRadius: "8px",
          overflow: "hidden",
          background: "white",
        },
        ".ck.ck-toolbar": {
          border: "none",
          borderBottom: "1px solid #E2E8F0",
        },
        ".ck.ck-content": {
          minHeight: "320px",
          padding: "16px",
        },
        ".ck.ck-content h1": {
          fontSize: "2rem",
          fontWeight: "700",
          lineHeight: "1.25",
          margin: "0",
        },
        ".ck.ck-content h2": {
          fontSize: "1.5rem",
          fontWeight: "700",
          lineHeight: "1.3",
          margin: "0",
        },
        ".ck.ck-content h3": {
          fontSize: "1.25rem",
          fontWeight: "600",
          lineHeight: "1.35",
          margin: "0",
        },
        ".ck.ck-content p": {
          margin: "0",
        },
        ".ck.ck-content h1 + p, .ck.ck-content h2 + p, .ck.ck-content h3 + p": {
          marginTop: "0",
        },
        ".ck.ck-content ul, .ck.ck-content ol": {
          paddingLeft: "28px",
          marginLeft: "8px",
          marginTop: "0.25rem",
          marginBottom: "0.25rem",
        },
        ".ck.ck-content li": {
          paddingLeft: "4px",
        },
        ".ck.ck-content table": {
          borderCollapse: "collapse",
          width: "100%",
        },
        ".ck.ck-content table td, .ck.ck-content table th": {
          border: "1px solid #CBD5E1",
          padding: "8px 10px",
        },
      }}
    >
      <CKEditor
        disabled={isDisabled}
        editor={ClassicEditor}
        data={value || ""}
        config={editorConfig}
        onReady={handleReady}
        onChange={(_, editor) => {
          onChange?.(editor.getData());
        }}
      />
    </Box>
  );
};

export default CompanyPolicyEditor;
